import React, { useEffect, useMemo, useRef, useState } from 'react';
import { LuListFilter, LuSearch, LuX } from 'react-icons/lu';
import DateRangeFilter, { DateRange } from './analytics/DateRangeFilter';
import {
  CommunityFilters,
  CommunityFilterFacets,
  SeverityLevel,
  SEVERITY_ORDER,
  ResolvedFilter,
  SearchField,
  countActiveFilters,
  defaultCommunityFilters,
} from '../types/communityFilters';

interface CommunityFilterPanelProps {
  open: boolean;
  filters: CommunityFilters;
  facets: CommunityFilterFacets;
  facetsLoading?: boolean;
  onChange: (next: CommunityFilters) => void;
  onClose: () => void;
}

const SEVERITY_META: Record<SeverityLevel, { label: string; color: string }> = {
  low: { label: 'Low', color: '#10B981' },
  medium: { label: 'Medium', color: '#FBBF24' },
  high: { label: 'High', color: '#F97316' },
  extreme: { label: 'Extreme', color: '#EF4444' },
};

const STATUS_OPTIONS: { key: ResolvedFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'unresolved', label: 'Open' },
  { key: 'resolved', label: 'Resolved' },
];

function todayISO(): string {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-deck-faint mb-2">
    {children}
  </h3>
);

const CommunityFilterPanel: React.FC<CommunityFilterPanelProps> = ({
  open,
  filters,
  facets,
  facetsLoading = false,
  onChange,
  onClose,
}) => {
  // Debounced search text so we don't refetch on every keystroke.
  const [searchDraft, setSearchDraft] = useState(filters.searchTerm);
  const [userQuery, setUserQuery] = useState('');
  const [regionQuery, setRegionQuery] = useState('');
  const panelRef = useRef<HTMLDivElement>(null);

  // Keep the draft in sync when the panel is (re)opened or filters reset externally.
  useEffect(() => {
    setSearchDraft(filters.searchTerm);
  }, [filters.searchTerm]);

  // Debounce search term -> parent filters.
  useEffect(() => {
    if (searchDraft === filters.searchTerm) return undefined;
    const t = setTimeout(() => {
      onChange({ ...filters, searchTerm: searchDraft });
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchDraft]);

  // Escape to close.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const dateValue: DateRange = {
    start: filters.dateStart,
    end: filters.dateEnd ?? todayISO(),
  };

  const handleDateChange = (v: DateRange) => {
    if (v.start === null) {
      onChange({ ...filters, dateStart: null, dateEnd: null });
    } else {
      onChange({ ...filters, dateStart: v.start, dateEnd: v.end });
    }
  };

  const toggleSeverity = (level: SeverityLevel) => {
    const has = filters.severities.includes(level);
    onChange({
      ...filters,
      severities: has
        ? filters.severities.filter(s => s !== level)
        : [...filters.severities, level],
    });
  };

  const toggleUser = (userId: string) => {
    const has = filters.userIds.includes(userId);
    onChange({
      ...filters,
      userIds: has ? filters.userIds.filter(u => u !== userId) : [...filters.userIds, userId],
    });
  };

  const toggleRegion = (region: string) => {
    const has = filters.regions.includes(region);
    onChange({
      ...filters,
      regions: has ? filters.regions.filter(r => r !== region) : [...filters.regions, region],
    });
  };

  const setSearchField = (field: SearchField) => {
    onChange({ ...filters, searchField: field });
  };

  const setStatus = (status: ResolvedFilter) => {
    onChange({ ...filters, status });
  };

  const filteredUsers = useMemo(() => {
    const q = userQuery.trim().toLowerCase();
    if (!q) return facets.users;
    return facets.users.filter(u => u.username.toLowerCase().includes(q));
  }, [facets.users, userQuery]);

  const filteredRegions = useMemo(() => {
    const q = regionQuery.trim().toLowerCase();
    if (!q) return facets.regions;
    return facets.regions.filter(r => r.toLowerCase().includes(q));
  }, [facets.regions, regionQuery]);

  const activeCount = countActiveFilters(filters);

  const resetAll = () => {
    setSearchDraft('');
    setUserQuery('');
    setRegionQuery('');
    onChange({ ...defaultCommunityFilters });
  };

  if (!open) return null;

  return (
    // z-index must clear Leaflet's map panes/controls (hoisted to ~1000) so the
    // drawer sits above the Geo map instead of behind it.
    <div className="fixed inset-0 z-[1100]" role="dialog" aria-modal="true" aria-label="Filter hazards">
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close filters"
        onClick={onClose}
        className="absolute inset-0 bg-black/40 backdrop-blur-[1px]"
      />

      {/* Slide-over panel */}
      <div
        ref={panelRef}
        className="absolute right-0 top-0 h-full w-full sm:w-[24rem] md:w-[27rem] bg-white dark:bg-deck-panel shadow-2xl border-l border-gray-200 dark:border-white/10 flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 h-16 border-b border-gray-200 dark:border-white/10 shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <LuListFilter className="w-5 h-5 text-[#FF3EB5] shrink-0" />
            <h2 className="text-base font-semibold text-gray-900 dark:text-white truncate">
              Filter hazards
            </h2>
            {activeCount > 0 && (
              <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 text-xs font-bold text-white bg-[#FF3EB5] rounded-full">
                {activeCount}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 -mr-2 rounded-lg text-gray-500 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/5"
            aria-label="Close"
          >
            <LuX className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-6">
          {/* My hazards */}
          <label className="flex items-center justify-between cursor-pointer">
            <span className="text-sm font-medium text-gray-800 dark:text-gray-100">
              My hazards only
            </span>
            <span className="relative inline-flex items-center">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={filters.myHazardsOnly}
                onChange={() => onChange({ ...filters, myHazardsOnly: !filters.myHazardsOnly })}
              />
              <span className="w-10 h-6 bg-gray-200 dark:bg-gray-600 rounded-full peer-checked:bg-[#FF3EB5] transition-colors" />
              <span className="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform peer-checked:translate-x-4" />
            </span>
          </label>

          {/* Search */}
          <div>
            <SectionLabel>Search</SectionLabel>
            <div className="inline-flex mb-2 rounded-lg bg-gray-100 dark:bg-gray-700 p-0.5">
              {(['description', 'tags'] as SearchField[]).map(field => (
                <button
                  key={field}
                  type="button"
                  onClick={() => setSearchField(field)}
                  className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                    filters.searchField === field
                      ? 'bg-white dark:bg-deck-panel text-[#FF3EB5] shadow-sm'
                      : 'text-gray-500 dark:text-gray-300'
                  }`}
                >
                  {field === 'description' ? 'Description' : 'Tags'}
                </button>
              ))}
            </div>
            <div className="relative">
              <LuSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchDraft}
                onChange={e => setSearchDraft(e.target.value)}
                placeholder={
                  filters.searchField === 'tags' ? 'Search tags…' : 'Search descriptions…'
                }
                className="w-full pl-9 pr-9 py-2 text-sm rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-white/10 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#FF3EB5]/40 focus:border-[#FF3EB5]"
              />
              {searchDraft && (
                <button
                  type="button"
                  onClick={() => setSearchDraft('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                  aria-label="Clear search"
                >
                  <LuX className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Date */}
          <div>
            <SectionLabel>Date</SectionLabel>
            <DateRangeFilter value={dateValue} onChange={handleDateChange} />
          </div>

          {/* Severity */}
          <div>
            <SectionLabel>Severity (community votes)</SectionLabel>
            <div className="flex flex-wrap gap-2">
              {SEVERITY_ORDER.map(level => {
                const active = filters.severities.includes(level);
                const meta = SEVERITY_META[level];
                return (
                  <button
                    key={level}
                    type="button"
                    onClick={() => toggleSeverity(level)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                      active
                        ? 'text-white border-transparent'
                        : 'text-gray-600 dark:text-gray-300 border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                    style={active ? { backgroundColor: meta.color } : undefined}
                  >
                    <span
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: active ? '#fff' : meta.color }}
                    />
                    {meta.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Status */}
          <div>
            <SectionLabel>Status</SectionLabel>
            <div className="inline-flex rounded-lg bg-gray-100 dark:bg-gray-700 p-0.5">
              {STATUS_OPTIONS.map(opt => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setStatus(opt.key)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    filters.status === opt.key
                      ? 'bg-white dark:bg-deck-panel text-[#FF3EB5] shadow-sm'
                      : 'text-gray-500 dark:text-gray-300'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Users */}
          <div>
            <SectionLabel>
              Users{filters.userIds.length > 0 ? ` (${filters.userIds.length})` : ''}
            </SectionLabel>
            {facets.users.length > 8 && (
              <div className="relative mb-2">
                <LuSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={userQuery}
                  onChange={e => setUserQuery(e.target.value)}
                  placeholder="Find a user…"
                  className="w-full pl-9 pr-3 py-1.5 text-sm rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-white/10 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#FF3EB5]/40"
                />
              </div>
            )}
            <div className="max-h-44 overflow-y-auto rounded-lg border border-gray-200 dark:border-white/10 divide-y divide-gray-100 dark:divide-white/5">
              {facetsLoading ? (
                <div className="px-3 py-3 text-sm text-gray-400">Loading users…</div>
              ) : filteredUsers.length === 0 ? (
                <div className="px-3 py-3 text-sm text-gray-400">No users found</div>
              ) : (
                filteredUsers.map(u => (
                  <label
                    key={u.userId}
                    className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-white/5"
                  >
                    <input
                      type="checkbox"
                      checked={filters.userIds.includes(u.userId)}
                      onChange={() => toggleUser(u.userId)}
                      className="h-4 w-4 accent-[#FF3EB5] rounded"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-200 truncate">
                      {u.username}
                    </span>
                  </label>
                ))
              )}
            </div>
          </div>

          {/* Region */}
          <div>
            <SectionLabel>
              Region{filters.regions.length > 0 ? ` (${filters.regions.length})` : ''}
            </SectionLabel>
            {facets.regions.length > 8 && (
              <div className="relative mb-2">
                <LuSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={regionQuery}
                  onChange={e => setRegionQuery(e.target.value)}
                  placeholder="Find a region…"
                  className="w-full pl-9 pr-3 py-1.5 text-sm rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-white/10 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#FF3EB5]/40"
                />
              </div>
            )}
            {facets.regions.length === 0 && !facetsLoading ? (
              <p className="text-sm text-gray-400">No region data available</p>
            ) : (
              <div className="max-h-44 overflow-y-auto rounded-lg border border-gray-200 dark:border-white/10 divide-y divide-gray-100 dark:divide-white/5">
                {facetsLoading ? (
                  <div className="px-3 py-3 text-sm text-gray-400">Loading regions…</div>
                ) : filteredRegions.length === 0 ? (
                  <div className="px-3 py-3 text-sm text-gray-400">No regions found</div>
                ) : (
                  filteredRegions.map(r => (
                    <label
                      key={r}
                      className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-white/5"
                    >
                      <input
                        type="checkbox"
                        checked={filters.regions.includes(r)}
                        onChange={() => toggleRegion(r)}
                        className="h-4 w-4 accent-[#FF3EB5] rounded"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-200 truncate">{r}</span>
                    </label>
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-gray-200 dark:border-white/10 shrink-0">
          <button
            type="button"
            onClick={resetAll}
            disabled={activeCount === 0}
            className={`text-sm font-medium transition-colors ${
              activeCount === 0
                ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                : 'text-gray-600 dark:text-gray-300 hover:text-[#FF3EB5]'
            }`}
          >
            Reset all
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2 text-sm font-semibold text-white bg-[#FF3EB5] hover:bg-[#E635A3] rounded-lg transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default CommunityFilterPanel;
